import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getChatUsersRequest,
  getChatConversationsRequest,
  getChatMessagesRequest,
  sendChatMessageRequest,
  startDMRequest,
  createGroupRequest,
  receiveSocketMessage,
} from "../redux/actions/chatAction";
import { Modal, Checkbox, Input, Button, notification, Popover } from "antd";
import useSocket from "../utils/hooks/useSocket";
import { getCurrentUser, uploadChatFileApi } from "../utils/chatApi";
import api from "../utils/axios";

const BRAND = "#884699";
const BRAND_DARK = "#6b2b80";
const BRAND_LIGHT = "#f5eef8";

const getTime = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const getConvDisplay = (conv, currentLoginID, usersById) => {
  if (conv.type === "group") {
    return {
      name: conv.groupName || "Group",
      initials: (conv.groupName || "GR").slice(0, 2).toUpperCase(),
      color: BRAND,
      bg: BRAND_LIGHT,
      isGroup: true,
      online: false,
    };
  }

  // without a known identity every participant "isn't me", which used to make this
  // fall through to participants[0] — the same person for every conversation
  const me = currentLoginID?.toString();
  const participants = conv.participants || [];
  const other = me
    ? participants.find((p) => (p?.LoginID ?? p)?.toString() !== me)
    : undefined;

  const otherId = (other?.LoginID ?? other)?.toString();
  // a freshly created DM can come back with bare ids, so fall back to the people list
  const known = otherId ? usersById?.[otherId] : undefined;

  const name =
    other?.employeename || other?.name || known?.employeename || known?.name || "Unknown";

  return {
    name,
    otherId,
    initials: name.slice(0, 2).toUpperCase(),
    color: "#2563eb",
    bg: "#eff6ff",
    isGroup: false,
    online: false,
  };
};

const resolveMediaUrl = (url, baseURL) =>
  url?.startsWith("http") ? url : `${(baseURL || "").replace("/api", "")}${url}`;

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];

const isImageUrl = (url) => {
  const ext = (url || "").split("?")[0].split(".").pop().toLowerCase();
  return IMAGE_EXTENSIONS.includes(ext);
};

const getFileName = (url) => {
  try {
    return decodeURIComponent((url || "").split("?")[0].split("/").pop()) || "Attachment";
  } catch {
    return "Attachment";
  }
};

const getLastPreview = (conv) => conv.lastMessage?.text || "No messages yet";
const getLastTime = (conv) => {
  if (!conv.lastMessage?.createdAt) return "";
  return new Date(conv.lastMessage.createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const PhoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.69a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const VideoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);
const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);
const AttachIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);
const EmojiIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
);
const GroupIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
  </svg>
);
const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const FileIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
  </svg>
);
const ChatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

function Avatar({ name, initials, color, bg, online, size = 40, showDot = true, isGroup = false }) {
  const fontSize = size < 36 ? 11 : 13;
  return (
    <div style={{ position: "relative", flexShrink: 0, width: size, height: size }}>
      <div style={{
        width: size, height: size, borderRadius: "50%",
        background: bg || "#f1f5f9", color: color || "#6366f1",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize, fontWeight: 600, letterSpacing: "0.02em",
        border: `1.5px solid ${color || "#6366f1"}22`,
      }}>
        {initials || (name ? name.slice(0, 2).toUpperCase() : "?")}
      </div>
      {showDot && !isGroup && (
        <span style={{
          position: "absolute", bottom: 1, right: 1,
          width: size < 36 ? 8 : 10, height: size < 36 ? 8 : 10,
          borderRadius: "50%",
          background: online ? "#22c55e" : "#94a3b8",
          border: "2px solid #fff",
        }} />
      )}
    </div>
  );
}

function TypingDots() {
  return (
    <div style={{ display: "flex", gap: 4, padding: "4px 2px" }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 6, height: 6, borderRadius: "50%", background: BRAND,
          display: "inline-block",
          animation: "chatBounce 1.2s infinite",
          animationDelay: `${i * 0.18}s`,
        }} />
      ))}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, padding: '20px' }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        border: `3px solid ${BRAND_LIGHT}`,
        borderTop: `3px solid ${BRAND}`,
        animation: "spin 0.8s linear infinite",
      }} />
    </div>
  );
}

const COMMON_EMOJIS = ["😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "😊", "😇", "🙂", "🙃", "😉", "😌", "😍", "🥰", "😘", "😗", "😙", "😚", "😋", "😛", "😝", "😜", "🤪", "🤨", "🧐", "🤓", "😎", "🤩", "🥳", "😏", "😒", "😞", "😔", "😟", "😕", "🙁", "☹️", "😣", "😖", "😫", "😩", "🥺", "😢", "😭", "😤", "😠", "😡", "🤬", "🤯", "😳", "🥵", "🥶", "😱", "😨", "😰", "😥", "😓", "🤗", "🤔", "🤭", "🤫", "🤥", "😶", "😐", "😑", "😬", "🙄", "😯", "😦", "😧", "😮", "😲", "🥱", "😴", "🤤", "😪", "😵", "🤐", "🥴", "🤢", "🤮", "🤧", "😷", "🤒", "🤕", "🤑", "🤠", "😈", "👿", "👹", "👺", "🤡", "💩", "👻", "💀", "☠️", "👽", "👾", "🤖", "🎃", "😺", "😸", "😹", "😻", "😼", "😽", "🙀", "😿", "😾"];

function EmojiPicker({ onSelect }) {
  return (
    <div className="tt-scroll" style={{ width: 280, height: 200, overflowY: "auto", display: "grid", gridTemplateColumns: "repeat(8, 1fr)", padding: 8, gap: 4, background: "#fff" }}>
      {COMMON_EMOJIS.map(emoji => (
        <button
          key={emoji}
          onMouseDown={e => e.preventDefault()}
          onClick={() => onSelect(emoji)}
          style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", padding: 4, borderRadius: 4, transition: "background 0.2s" }}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

export default function ChatApp() {
  const dispatch = useDispatch();
  const { getSocket, socket } = useSocket();

  const {
    conversations = [],
    conversationsLoading,
    users = [],
    usersLoading,
    messages = [],
    messagesLoading,
    activeConversationId,
    messagesPage,
    hasMoreMessages,
    sendMessageLoading: sending,
    usersError,
    conversationsError,
  } = useSelector((state) => state.chat || {});

  const [onlineUserIds, setOnlineUserIds] = useState([]);

  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sidebarTab, setSidebarTab] = useState("chats");

  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);

  const bottomRef = useRef(null);
  const messagesRef = useRef(null);
  // set while a "load older" page is in flight, so we restore scroll instead of jumping to the bottom
  const pendingOlderRef = useRef(null);
  // consumed by the auto-scroll effect: a prepend must not pull the view back down
  const skipAutoScrollRef = useRef(false);
  const textareaRef = useRef(null);
  const typingTimer = useRef(null);
  const fileInputRef = useRef(null);

  const currentUser = useMemo(() => getCurrentUser(), []);

  const usersById = useMemo(() => {
    const map = {};
    (users || []).forEach((u) => {
      const id = u?.LoginID?.toString();
      if (id) map[id] = u;
    });
    return map;
  }, [users]);

  useEffect(() => {
    dispatch(getChatConversationsRequest());
    dispatch(getChatUsersRequest());
  }, [dispatch]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const handleUsersOnline = (ids) => {
      if (Array.isArray(ids)) {
        setOnlineUserIds(ids.map(id => id.toString()));
      }
    };

    const handleUserOnline = (data) => {
      if (data?.userId) {
        setOnlineUserIds(prev => prev.includes(data.userId.toString()) ? prev : [...prev, data.userId.toString()]);
      }
    };

    const handleUserOffline = (data) => {
      if (data?.userId) {
        setOnlineUserIds(prev => prev.filter(id => id !== data.userId.toString()));
      }
    };

    const handleMessageReceive = (data) => {
      if (data?.message && data?.conversationId) {
        dispatch(receiveSocketMessage(data.message, data.conversationId));
        if (data.message?.sender?.LoginID?.toString() !== currentUser?.LoginID?.toString()) {
          setIsTyping(false);
        }
      }
    };

    const handleTypingStart = (data) => {
      if (data?.conversationId === activeConversationId && data.userId?.toString() !== currentUser?.LoginID?.toString()) {
        setIsTyping(true);
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => setIsTyping(false), 5000);
      }
    };

    const handleTypingStop = (data) => {
      if (data?.conversationId === activeConversationId) {
        setIsTyping(false);
      }
    };

    socket.on("users:online", handleUsersOnline);
    socket.on("user:online", handleUserOnline);
    socket.on("user:offline", handleUserOffline);
    socket.on("message:receive", handleMessageReceive);
    socket.on("typing:start", handleTypingStart);
    socket.on("typing:stop", handleTypingStop);

    return () => {
      socket.off("users:online", handleUsersOnline);
      socket.off("user:online", handleUserOnline);
      socket.off("user:offline", handleUserOffline);
      socket.off("message:receive", handleMessageReceive);
      socket.off("typing:start", handleTypingStart);
      socket.off("typing:stop", handleTypingStop);
      clearTimeout(typingTimer.current);
    };
  }, [getSocket, socket, activeConversationId, currentUser?.LoginID, dispatch]);

  useEffect(() => {
    const socket = getSocket();
    if (socket && conversations.length > 0) {
      conversations.forEach(c => {
        if (c?._id) socket.emit("conversation:join", { conversationId: c._id });
      });
    }
  }, [getSocket, conversations]);

  // restore the scroll offset after older messages are prepended
  useLayoutEffect(() => {
    const el = messagesRef.current;
    const prevHeight = pendingOlderRef.current;
    if (el && prevHeight != null) {
      el.scrollTop = el.scrollHeight - prevHeight;
      pendingOlderRef.current = null;
      // this layout effect runs before the auto-scroll effect below, so flag it off
      skipAutoScrollRef.current = true;
    }
  }, [messages]);

  useEffect(() => {
    // a prepend keeps the reader where they were; only new arrivals scroll down
    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false;
      return;
    }
    if (pendingOlderRef.current != null) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeConversationId, messages]);

  const handleMessagesScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollTop > 60) return;
    if (!activeConversationId || !hasMoreMessages || messagesLoading) return;
    if (pendingOlderRef.current != null) return;

    pendingOlderRef.current = el.scrollHeight;
    dispatch(getChatMessagesRequest(activeConversationId, (messagesPage || 1) + 1));
  }, [activeConversationId, hasMoreMessages, messagesLoading, messagesPage, dispatch]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 96) + "px";
  }, [input]);

  const selectConversation = useCallback((convId) => {
    dispatch(getChatMessagesRequest(convId));
    setSidebarTab("chats");
    const socket = getSocket();
    if (socket) socket.emit("conversation:join", { conversationId: convId });
  }, [dispatch, getSocket]);

  const handleStartDM = useCallback((userId) => {
    dispatch(startDMRequest(userId));
    setSidebarTab("chats");
  }, [dispatch]);

  const handleSend = useCallback(async (textOverride = null, mediaUrl = null) => {
    const text = textOverride !== null ? textOverride : input.trim();
    if (!text && !mediaUrl) return;
    if (!activeConversationId || sending) return;

    dispatch(sendChatMessageRequest(activeConversationId, text, mediaUrl));

    const socket = getSocket();
    if (socket) socket.emit("typing:stop", { conversationId: activeConversationId });
    clearTimeout(typingTimer.current);

    if (textOverride === null) setInput("");
  }, [input, activeConversationId, sending, dispatch, getSocket]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const res = await uploadChatFileApi(file);
      // the chat API normally wraps payloads in data.data, but tolerate a flat shape
      const url = res.data?.data?.url ?? res.data?.url;

      if (url) {
        handleSend("", url);
      } else {
        notification.error({
          message: "Upload failed",
          description: res.data?.message || "The server did not return a file URL.",
        });
      }
    } catch (err) {
      notification.error({
        message: "Upload failed",
        description: err?.response?.data?.message || err.message,
      });
    }
    e.target.value = "";
  };

  const handleCreateGroup = () => {
    if (!groupName.trim() || selectedMembers.length === 0) {
      return notification.warning({ message: "Please enter a name and select members" });
    }
    dispatch(createGroupRequest(groupName, selectedMembers));
    setIsGroupModalOpen(false);
    setGroupName("");
    setSelectedMembers([]);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    const socket = getSocket();
    if (socket && activeConversationId) {
      socket.emit("typing:start", { conversationId: activeConversationId });
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => {
        socket.emit("typing:stop", { conversationId: activeConversationId });
      }, 3000);
    }
  };

  const filteredConversations = useMemo(() => {
    return (conversations || []).filter((c) => {
      const display = getConvDisplay(c, currentUser?.LoginID, usersById);
      return display.name.toLowerCase().includes(search.toLowerCase());
    });
  }, [conversations, currentUser?.LoginID, search, usersById]);

  const filteredUsers = useMemo(() => {
    return (users || []).filter(u => {
      const uId = u?.LoginID?.toString();
      const cId = currentUser?.LoginID?.toString();
      const matchesSearch = (u?.employeename || u?.name || "").toLowerCase().includes(search.toLowerCase());
      return uId !== cId && matchesSearch;
    });
  }, [users, currentUser?.LoginID, search]);

  const activeConv = conversations.find((c) => c._id === activeConversationId);
  const activeDisplay = useMemo(() => {
    if (!activeConv) return null;
    const d = getConvDisplay(activeConv, currentUser?.LoginID, usersById);
    if (!d.isGroup) {
      d.online = onlineUserIds.includes(d.otherId);
    }
    return d;
  }, [activeConv, currentUser?.LoginID, onlineUserIds, usersById]);

  const activeMessages = activeConversationId ? messages : [];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes chatBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40%            { transform: translateY(-5px); opacity: 1; }
        }
        @keyframes msgIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .chat-contact:hover  { background: #f8f3fb !important; }
        .chat-contact.active { background: ${BRAND_LIGHT} !important; border-left: 3px solid ${BRAND} !important; }

        .sidebar-tab-btn {
            flex: 1; border: none; background: none; padding: 12px 0;
            font-size: 13px; font-weight: 600; color: #94a3b8;
            cursor: pointer; position: relative; transition: color 0.2s;
        }
        .sidebar-tab-btn.active { color: ${BRAND}; }
        .sidebar-tab-btn.active::after {
            content: ""; position: absolute; bottom: 0; left: 20%; right: 20%;
            height: 3px; background: ${BRAND}; border-top-left-radius: 4px; border-top-right-radius: 4px;
        }

        .chat-icon-btn {
          background: none; border: none; cursor: pointer;
          width: 34px; height: 34px; border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          color: #64748b; transition: background 0.15s, color 0.15s;
        }
        .chat-icon-btn:hover { background: #f1f5f9; color: #1e293b; }

        .chat-send-btn {
          width: 38px; height: 38px; border-radius: 11px; border: none;
          background: ${BRAND}; cursor: pointer; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.15s, transform 0.12s;
        }
        .chat-send-btn:hover:not(:disabled) { background: ${BRAND_DARK}; }
        .chat-send-btn:active:not(:disabled) { transform: scale(0.94); }
        .chat-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .chat-textarea {
          flex: 1; border: 1.5px solid #e8dff0; border-radius: 12px;
          padding: 9px 13px; font-size: 13.5px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #faf7fd; color: #1e1226; resize: none;
          line-height: 1.5; min-height: 40px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .chat-textarea:focus { outline: none; border-color: ${BRAND}; box-shadow: 0 0 0 3px rgba(136,70,153,0.1); background: #fff; }
        .chat-textarea::placeholder { color: #bba8cc; }

        .search-input {
          width: 100%; padding: 8px 10px 8px 32px;
          font-size: 13px; border: 1.5px solid #ede6f5;
          border-radius: 10px; background: #faf5fc;
          color: #1e1226; font-family: 'Plus Jakarta Sans', sans-serif;
          transition: border-color 0.2s;
        }
        .search-input:focus { outline: none; border-color: ${BRAND}; background: #fff; }
        .search-input::placeholder { color: #c5b2d4; }

        .msg-bubble { animation: msgIn 0.2s ease both; }

        .tt-scroll::-webkit-scrollbar { width: 4px; }
        .tt-scroll::-webkit-scrollbar-thumb { background: rgba(136,70,153,0.2); border-radius: 4px; }
        .tt-scroll::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", display: "flex", height: "100%", minHeight: 0, background: "#f4eef8", overflow: "hidden" }}>

        <div style={{
          width: 300, minWidth: 300,
          display: "flex", flexDirection: "column",
          background: "#fff",
          borderRight: "1px solid #ede6f5",
          height: "100%",
          overflow: "hidden",
        }}>

          {/* Sidebar header */}
          <div style={{ padding: "18px 16px 4px", borderBottom: "1px solid #f0e8f5" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 10,
                  background: BRAND, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <ChatIcon />
                </div>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#1e1226", letterSpacing: "-0.3px" }}>Messages</span>
              </div>
              <button className="chat-icon-btn" title="New group" style={{ color: BRAND }} onClick={() => setIsGroupModalOpen(true)}>
                <PlusIcon />
              </button>
            </div>

            {/* Search */}
            <div style={{ position: "relative", marginBottom: 12 }}>
              <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#c5b2d4", pointerEvents: "none" }}>
                <SearchIcon />
              </span>
              <input
                className="search-input"
                type="text"
                placeholder={sidebarTab === "chats" ? "Search conversations..." : "Search people..."}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", borderBottom: "1px solid #f0e8f5", margin: "0 -16px" }}>
              <button
                className={`sidebar-tab-btn ${sidebarTab === "chats" ? "active" : ""}`}
                onClick={() => setSidebarTab("chats")}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <ChatIcon />
                  Chats
                </div>
              </button>
              <button
                className={`sidebar-tab-btn ${sidebarTab === "users" ? "active" : ""}`}
                onClick={() => setSidebarTab("users")}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <UsersIcon />
                  People
                </div>
              </button>
            </div>
          </div>

          {/* Sidebar Content */}
          <div className="tt-scroll" style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "8px 0" }}>
            {sidebarTab === "chats" ? (
              <>
                {conversationsLoading && <LoadingSpinner />}
                {filteredConversations.map(conv => {
                  const d = getConvDisplay(conv, currentUser?.LoginID, usersById);
                  if (!d.isGroup) {
                    d.online = onlineUserIds.includes(d.otherId);
                  }
                  return (
                    <div
                      key={conv._id}
                      className={`chat-contact${activeConversationId === conv._id ? " active" : ""}`}
                      onClick={() => selectConversation(conv._id)}
                      style={{
                        display: "flex", alignItems: "center", gap: 11,
                        padding: "11px 14px 11px 16px",
                        cursor: "pointer", transition: "background 0.15s",
                        borderLeft: "3px solid transparent",
                      }}
                    >
                      <Avatar {...d} size={44} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: "#1e1226", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {d.name}
                          </span>
                          <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}>{getLastTime(conv)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2 }}>
                          <span style={{ fontSize: 12.5, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>
                            {getLastPreview(conv)}
                          </span>
                          {conv.unreadCount > 0 && (
                            <span style={{
                              background: BRAND, color: "#fff", fontSize: 10, fontWeight: 700,
                              minWidth: 18, height: 18, borderRadius: 9, display: "flex",
                              alignItems: "center", justifyContent: "center", marginLeft: 6, padding: "0 4px"
                            }}>
                              {conv.unreadCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!conversationsLoading && filteredConversations.length === 0 && (
                  <div style={{ padding: "40px 16px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                    No conversations yet
                  </div>
                )}
              </>
            ) : (
              <>
                {usersLoading && <LoadingSpinner />}
                {filteredUsers.map(user => {
                  const isOnline = onlineUserIds.includes(user.LoginID?.toString());
                  return (
                    <div
                      key={user.LoginID}
                      className="chat-contact"
                      onClick={() => handleStartDM(user.LoginID)}
                      style={{
                        display: "flex", alignItems: "center", gap: 11,
                        padding: "11px 14px 11px 16px",
                        cursor: "pointer", transition: "background 0.15s",
                      }}
                    >
                      <Avatar
                        name={user.employeename || user.name}
                        online={isOnline}
                        size={40}
                        color={isOnline ? BRAND : "#6366f1"}
                        bg={isOnline ? BRAND_LIGHT : "#f1f5f9"}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#1e1226" }}>{user.employeename || user.name}</div>
                        <div style={{ fontSize: 12, color: isOnline ? "#22c55e" : "#94a3b8", fontWeight: 500 }}>
                          {isOnline ? "Active now" : "Offline"}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!usersLoading && filteredUsers.length === 0 && (
                  <div style={{ padding: "40px 16px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                    {usersError ? `Error: ${usersError}` : "No users found"}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar footer */}
          <div style={{ padding: "12px 16px", borderTop: "1px solid #f0e8f5", display: "flex", alignItems: "center", gap: 10, background: "#fff" }}>
            <Avatar
              name={currentUser?.employeename}
              online={true}
              size={36}
              color={BRAND}
              bg={BRAND_LIGHT}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1e1226" }}>{currentUser?.employeename || "You"}</div>
              <div style={{ fontSize: 11.5, color: "#22c55e", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", display: "inline-block" }} />
                Connected
              </div>
            </div>
            <button className="chat-icon-btn" onClick={() => dispatch(getChatUsersRequest())} title="Refresh users">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-6" /><path d="M1 20v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
          </div>
        </div>

        {/* MAIN CHAT */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, height: "100%", overflow: "hidden", background: "#faf7fd" }}>
          {!activeConversationId ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#c5b2d4", gap: 16 }}>
              <div style={{
                width: 80, height: 80, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 8px 30px rgba(136, 70, 153, 0.08)"
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="1.5">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div style={{ textAlign: "center" }}>
                <h3 style={{ color: "#1e1226", fontSize: 18, marginBottom: 8 }}>Select a conversation</h3>
                <p style={{ color: "#94a3b8", fontSize: 14 }}>Choose a contact from the sidebar to start chatting</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{
                padding: "13px 20px", background: "#fff",
                borderBottom: "1px solid #ede6f5",
                display: "flex", alignItems: "center", gap: 13,
                boxShadow: "0 1px 3px rgba(0,0,0,0.02)"
              }}>
                <Avatar {...activeDisplay} size={44} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1e1226", letterSpacing: "-0.2px" }}>
                    {activeDisplay.name}
                  </div>
                  <div style={{ fontSize: 12.5, marginTop: 1, color: isTyping ? BRAND : (activeDisplay.online ? "#22c55e" : "#94a3b8") }}>
                    {isTyping ? "typing…" : activeDisplay.online ? "Online" : "Offline"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button className="chat-icon-btn"><PhoneIcon /></button>
                  <button className="chat-icon-btn"><VideoIcon /></button>
                  <button className="chat-icon-btn"><InfoIcon /></button>
                </div>
              </div>

              {/* Messages Area */}
              <div
                className="tt-scroll"
                ref={messagesRef}
                onScroll={handleMessagesScroll}
                style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "24px 24px 12px", display: "flex", flexDirection: "column", gap: 4 }}
              >
                {messagesLoading && activeMessages.length > 0 && (
                  <div style={{ padding: "4px 0 12px" }}><LoadingSpinner /></div>
                )}
                {messagesLoading && activeMessages.length === 0 && <LoadingSpinner />}
                {!messagesLoading && activeMessages.length === 0 && (
                  <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                    <p style={{ fontSize: 13 }}>No messages yet. Say hi! 👋</p>
                  </div>
                )}
                {activeMessages.map((msg, i) => {
                  const isMe = (msg.sender?.LoginID || msg.sender)?.toString() === currentUser?.LoginID?.toString();
                  const showAvatar = !isMe && (i === 0 || (activeMessages[i - 1]?.sender?.LoginID || activeMessages[i - 1]?.sender)?.toString() !== (msg.sender?.LoginID || msg.sender)?.toString());
                  const senderName = msg.sender?.employeename || msg.sender?.name || "Other";

                  return (
                    <div key={msg._id || i} className="msg-bubble" style={{
                      display: "flex",
                      flexDirection: isMe ? "row-reverse" : "row",
                      alignItems: "flex-end",
                      gap: 8,
                      marginBottom: 6,
                      marginTop: 0,
                    }}>
                      {!isMe && (
                        <div style={{ width: 30, flexShrink: 0 }}>
                          {showAvatar && (
                            <Avatar
                              name={senderName}
                              size={30}
                              showDot={false}
                            />
                          )}
                        </div>
                      )}

                      <div style={{ maxWidth: "65%", display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start", gap: 3 }}>
                        <div style={{
                          padding: "10px 15px",
                          borderRadius: isMe ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                          background: isMe ? BRAND : "#fff",
                          color: isMe ? "#fff" : "#1e1226",
                          fontSize: 14, lineHeight: 1.5,
                          boxShadow: isMe ? "0 4px 12px rgba(136,70,153,0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
                          border: isMe ? "none" : "1px solid #ede6f5",
                        }}>
                          {msg.mediaUrl ? (
                            <div style={{ marginBottom: msg.text ? 8 : 0 }}>
                              {(() => {
                                const href = resolveMediaUrl(msg.mediaUrl, api.defaults.baseURL);
                                return isImageUrl(msg.mediaUrl) ? (
                                  <img
                                    src={href}
                                    alt="attachment"
                                    style={{ maxWidth: "100%", borderRadius: 8, display: "block", cursor: "pointer" }}
                                    onClick={() => window.open(href, "_blank", "noopener")}
                                    onError={(e) => { e.target.src = "https://placehold.co/200x200?text=Image+Not+Found"; }}
                                  />
                                ) : (
                                  <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      display: "flex", alignItems: "center", gap: 8,
                                      padding: "8px 10px", borderRadius: 10,
                                      background: isMe ? "rgba(255,255,255,0.18)" : "#f6f1fa",
                                      color: isMe ? "#fff" : "#1e1226",
                                      textDecoration: "none", maxWidth: 240,
                                    }}
                                  >
                                    <FileIcon />
                                    <span style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {getFileName(msg.mediaUrl)}
                                    </span>
                                  </a>
                                );
                              })()}
                            </div>
                          ) : null}
                          {msg.text}
                        </div>
                        <span style={{ fontSize: 10.5, color: "#94a3b8", padding: "0 4px" }}>
                          {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : getTime()}
                          {isMe && <span style={{ marginLeft: 4, color: "#bb93cc" }}>✓✓</span>}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {isTyping && (
                  <div className="msg-bubble" style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 30, flexShrink: 0 }}>
                      <Avatar name={activeDisplay.name} size={30} showDot={false} />
                    </div>
                    <div style={{ padding: "10px 14px", borderRadius: "16px 16px 16px 4px", background: "#fff", border: "1px solid #ede6f5" }}>
                      <TypingDots />
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div style={{ padding: "16px 20px 20px", background: "#fff", borderTop: "1px solid #ede6f5" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                  <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} accept="image/*,application/pdf" />
                  <button className="chat-icon-btn" onClick={() => fileInputRef.current?.click()}><AttachIcon /></button>

                  <Popover
                    content={<EmojiPicker onSelect={(emoji) => { setInput(prev => prev + emoji); setIsEmojiOpen(false); }} />}
                    trigger="click"
                    open={isEmojiOpen}
                    onOpenChange={setIsEmojiOpen}
                    placement="topRight"
                  >
                    <button className="chat-icon-btn"><EmojiIcon /></button>
                  </Popover>

                  <textarea
                    ref={textareaRef}
                    className="chat-textarea"
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={e => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                    }}
                    placeholder={`Type a message...`}
                    rows={1}
                  />
                  <button
                    className="chat-send-btn"
                    onClick={() => handleSend()}
                    disabled={!input.trim() || sending}
                  >
                    {sending ? <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #fff', borderTopColor: 'transparent', animation: 'spin 1s linear infinite' }} /> : <SendIcon />}
                  </button>
                </div>
              </div>

            </>
          )}
        </div>
      </div>

      {/* Group Modal */}
      <Modal
        title="Create New Group"
        open={isGroupModalOpen}
        onCancel={() => setIsGroupModalOpen(false)}
        onOk={handleCreateGroup}
        okText="Create Group"
        okButtonProps={{ style: { background: BRAND } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "10px 0" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Group Name</div>
            <Input
              placeholder="Enter group name..."
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
            />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Select Members</div>
            <div className="tt-scroll" style={{ maxHeight: 250, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 8, padding: 8 }}>
              <Checkbox.Group
                style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8 }}
                value={selectedMembers}
                onChange={setSelectedMembers}
              >
                {users.filter(u => u.LoginID?.toString() !== currentUser?.LoginID?.toString()).map(user => (
                  <div key={user.LoginID} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 8px" }}>
                    <Checkbox value={user.LoginID} />
                    <Avatar name={user.employeename || user.name} size={30} showDot={false} />
                    <span style={{ fontSize: 14 }}>{user.employeename || user.name}</span>
                  </div>
                ))}
              </Checkbox.Group>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
